'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { trpc } from '@/lib/trpc'
import { getVisitorId } from '@/hooks/use-visitor'
import { trackEvent } from '@/lib/analytics'

/**
 * Hook to get the variant for an experiment on the current page.
 * Returns the variant config if an experiment is active, or null.
 */
export function useExperiment(sectionId: string) {
  const pathname = usePathname()
  const visitorId = getVisitorId()
  const hasTrackedImpression = useRef(false)

  const { data, isLoading } = trpc.admin.getVariant.useQuery(
    { path: pathname, visitorId, sectionId },
    {
      enabled: !!visitorId && typeof window !== 'undefined',
      staleTime: 5 * 60 * 1000, // 5 min cache
      retry: false,
    }
  )

  // Track impression on first load
  useEffect(() => {
    if (data?.experimentId && data?.variant && !hasTrackedImpression.current) {
      hasTrackedImpression.current = true

      // Record impression via API
      fetch('/api/analytics/experiment-event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          experimentId: data.experimentId,
          visitorId,
          variant: data.variant,
          eventType: 'impression',
        }),
      }).catch(() => {})

      // GA4 mirroring
      trackEvent('experiment_impression', {
        experiment_id: data.experimentId,
        experiment_slug: data.slug || '',
        variant: data.variant,
        section: sectionId,
      })
    }
  }, [data, visitorId, sectionId])

  // Reset impression tracking on pathname change
  useEffect(() => {
    hasTrackedImpression.current = false
  }, [pathname])

  return {
    variant: data?.variant ?? null,
    variantConfig: data?.variantConfig ?? null,
    experimentId: data?.experimentId ?? null,
    isLoading,
  }
}
